import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ApartmentIcon from '@mui/icons-material/Apartment';
import GroupIcon from '@mui/icons-material/Group';
import GroupsIcon from '@mui/icons-material/Groups';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import SellIcon from '@mui/icons-material/Sell';
import SettingsIcon from '@mui/icons-material/Settings';
import { Roles, Routes } from 'enums';
import { useUserData } from 'hooks/use-user-data';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountManagementTab, AccountManagementTabs } from './account-management.types';

/**
 * Account-Bereiche je nach Rolle. Die Liste steht im linken Kontextpanel (nav-config.tsx);
 * die Seite selbst prüft damit, welcher Reiter aus der URL erlaubt ist.
 */
export const useAccountManagementTabs = (): AccountManagementTab[] => {
  const { t } = useTranslation();
  const { role, isPlatformAdmin } = useUserData();

  const tabs = useMemo((): AccountManagementTab[] => {
    const baseTabs = [
      { label: t('myProfile'), value: AccountManagementTabs.PROFILE, icon: <SettingsIcon /> },
      // Every role decides for itself which Mitteilungen and e-mails it gets.
      { label: t('engPrefsTab'), value: AccountManagementTabs.NOTIFICATIONS, icon: <NotificationsNoneIcon /> },
    ];

    if (role === Roles.MANAGER) {
      baseTabs.push(
        { label: t('myCompany'), value: AccountManagementTabs.COMPANY, icon: <ApartmentIcon /> },
        { label: t('myUsers'), value: AccountManagementTabs.USERS, icon: <GroupIcon /> },
        { label: t('myLicenses'), value: AccountManagementTabs.LICENSES, icon: <SellIcon /> }
      );
    }

    if (role === Roles.AGENCY) {
      baseTabs.push(
        { label: t('myCompany'), value: AccountManagementTabs.COMPANY, icon: <ApartmentIcon /> },
        { label: t('myClients'), value: AccountManagementTabs.TENANTS, icon: <GroupsIcon /> },
        { label: t('myUsers'), value: AccountManagementTabs.USERS, icon: <GroupIcon /> },
        { label: t('myLicenses'), value: AccountManagementTabs.LICENSES, icon: <SellIcon /> },
      );
    }

    if (role === Roles.ADMIN) {
      baseTabs.push(
        { label: t('myCompany'), value: AccountManagementTabs.COMPANY, icon: <ApartmentIcon /> },
        { label: t('myUsers'), value: AccountManagementTabs.USERS, icon: <GroupIcon /> },
        { label: t('myLicenses'), value: AccountManagementTabs.LICENSES, icon: <SellIcon /> }
      );
    }

    if (role === Roles.OWNER) {
      baseTabs.push(
        { label: t('myCompany'), value: AccountManagementTabs.COMPANY, icon: <ApartmentIcon /> },
        // The admin panel is platform-wide, so it follows platform standing —
        // not the `owner` role, which a tenant can hand out inside its own
        // workspace.
        ...(isPlatformAdmin
          ? [{ label: t('adminPanel'), value: AccountManagementTabs.ADMIN_PANEL, icon: <AdminPanelSettingsIcon /> }]
          : []),
        { label: t('myUsers'), value: AccountManagementTabs.USERS, icon: <GroupIcon /> },
        { label: t('myLicenses'), value: AccountManagementTabs.LICENSES, icon: <SellIcon /> }
      );
    }

    return baseTabs;
  }, [t, role, isPlatformAdmin]);

  return tabs;
};

/** Jeder Reiter hat eine eigene URL, damit das Kontextpanel ihn als aktiv erkennt. */
export const getAccountTabRoute = (tab: AccountManagementTabs): string => `${Routes.ACCOUNT}/${tab}`;
